import React, { useMemo } from 'react'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView, CustomTrace, getChartsEditingSettings, LoadingState, RechartData, useLegendState } from 'src/Libs/chart-kit'
import SystemChart from 'src/Libs/system-charts/shared/components/SystemChart'
import { SaveChartProps } from 'src/Libs/system-data-handler/types'
import { useUserSettings } from 'src/Hooks'
import { DASHBOARD_POC_SAVINGS_PIE_CHART_ID, DASHBOARD_SAVINGS_PIE_CHART_ID } from 'src/Libs/qa/ids'
import { TraceKey } from 'src/Libs/system-charts/shared'
import { getCostSavingsPieChartPreset } from 'src/Libs/system-charts/default-charts/utils/initial-chart-config'
import useChartLayout from 'src/Libs/chart-kit/shared/hooks/use-chart-layout'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import type { CostSavingChartTraceKey } from '../../cost-saving-chart-trace-keys'

interface Props {
  data: RechartData
  customTraces: CustomTrace[]
  labelTraces: TraceKey[] | CostSavingChartTraceKey[]
  from: Moment
  to: Moment
  view: ChartView
  resolution?: ChartView
  height: number
  legendHeight: number
  handleSaveChart: (props: SaveChartProps) => void
  showForecast: boolean
  setBiggestLegendHeight?: (height: number) => void
  loadingState: LoadingState
  isDynamicPowerPriceEnabled?: boolean
  isDynamicEmsActive?: boolean
}

/**
 * Pie chart that displays the savings of the user
 */
const SavingsPieChart: React.FC<Props> = ({
  data,
  customTraces,
  labelTraces,
  from,
  to,
  view,
  resolution,
  loadingState,
  showForecast,
  legendHeight,
  setBiggestLegendHeight,
  height,
  handleSaveChart,
  isDynamicPowerPriceEnabled = false,
  isDynamicEmsActive = false,
}) => {
  const { t } = useCustomTranslation()
  const { userSettings } = useUserSettings()
  const initialChartLayout = useMemo(
    () => getCostSavingsPieChartPreset(height, customTraces, labelTraces as CostSavingChartTraceKey[]),
    [height, customTraces, labelTraces]
  )
  const chartSettingsList = useMemo(() => getChartsEditingSettings(userSettings), [userSettings])
  const { chartLayout } = useChartLayout(chartSettingsList, initialChartLayout)
  const chartLayoutId = chartLayout.id ?? ''
  const { saveLegendState, hideLegend } = useLegendState(`${chartLayoutId}`)
  const dataQa = data.isPocChart ? DASHBOARD_POC_SAVINGS_PIE_CHART_ID : DASHBOARD_SAVINGS_PIE_CHART_ID

  const infoText =
    isDynamicPowerPriceEnabled && isDynamicEmsActive
      ? t('system-charts.savings-pie-chart.tooltip-ai-active')
      : isDynamicPowerPriceEnabled && !isDynamicEmsActive
      ? t('system-charts.savings-pie-chart.tooltip-ai-inactive')
      : undefined

  return (
    <SystemChart
      view={view}
      resolution={resolution}
      from={from}
      to={to}
      showForecast={showForecast}
      loadingState={loadingState}
      rechartData={data}
      customLayout={chartLayout}
      handleSaveChart={props => handleSaveChart({ customLayout: chartLayout, chartSettings: props })}
      maxHeight={chartLayout.maxHeight}
      isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
      hideLegend={hideLegend}
      toggleHideLegend={() => saveLegendState()}
      dataQa={dataQa}
      setBiggestLegendHeight={setBiggestLegendHeight}
      fixedLegendHeight={legendHeight}
      isCostSavingsPieChart
      pieChartTooltipText={infoText}
    />
  )
}

export default SavingsPieChart
